import { Observable, WritableObservable } from "micro-observables";
import { ApplicationDraft } from "@domain/application/applicationDraft";
import { Country } from "@domain/currentProfile/countryCode";
import { Media } from "@domain/media/media";
import { ApplicationDegree } from "@domain/program/programDraft";

export const applicationEditionSteps = [
	"STUDY_CHOICES",
	"PERSONAL_INFORMATIONS",
	"UPLOAD_DOCUMENTS",
	"VIDEO_STORY",
	"SUMMARY",
] as const;
export type ApplicationEditionStep = (typeof applicationEditionSteps)[number];

export class ApplicationEditorController {
	private previousValues: ApplicationDraft[];
	private readonly currentValueIndex = new WritableObservable(0);

	readonly application: Observable<ApplicationDraft>;
	private _step = new WritableObservable<ApplicationEditionStep>("STUDY_CHOICES");
	readonly step = this._step.readOnly();

	constructor(initialApplicationDraft: ApplicationDraft) {
		this.previousValues = [initialApplicationDraft];
		this.application = this.currentValueIndex.transform((index) => this.previousValues[index]);
	}

	next(): void {
		const step = this.step.get();

		switch (step) {
			case "STUDY_CHOICES":
				this._step.set("PERSONAL_INFORMATIONS");
				break;

			case "PERSONAL_INFORMATIONS":
				this._step.set("UPLOAD_DOCUMENTS");
				break;
			case "UPLOAD_DOCUMENTS":
				this._step.set("VIDEO_STORY");
				break;
			case "VIDEO_STORY":
				this._step.set("SUMMARY");
				break;
			default:
				break;
		}
	}

	previous(): void {
		const step = this.step.get();
		switch (step) {
			case "SUMMARY":
				this._step.set("VIDEO_STORY");
				break;
			case "VIDEO_STORY":
				this._step.set("UPLOAD_DOCUMENTS");
				break;
			case "UPLOAD_DOCUMENTS":
				this._step.set("PERSONAL_INFORMATIONS");
				break;

			case "PERSONAL_INFORMATIONS":
				this._step.set("STUDY_CHOICES");
				break;
			default:
				break;
		}
	}

	setStep(step: ApplicationEditionStep): void {
		this._step.set(step);
	}

	updateApplication(applicationUpdate: Partial<ApplicationDraft>): void {
		const application: ApplicationDraft = { ...this.application.get(), ...applicationUpdate };
		this.previousValues = [...this.previousValues.slice(0, this.currentValueIndex.get() + 1), application];
		this.currentValueIndex.set(this.currentValueIndex.get() + 1);
	}

	setFirstName(firstName: string): void {
		this.updateApplication({ firstName });
	}
	setLastName(familyName: string): void {
		this.updateApplication({ familyName });
	}
	setDateOfBirth(dateBirth: Date): void {
		this.updateApplication({ dateBirth });
	}
	setCountry(countryResidence: Country): void {
		this.updateApplication({ countryResidence });
	}
	setNationality(nationality: string): void {
		this.updateApplication({ nationality });
	}
	setEmail(email: string): void {
		this.updateApplication({ email });
	}

	setPhoneNumber(phoneNumber: string): void {
		this.updateApplication({ phoneNumber });
	}

	setIndentity(media?: Media): void {
		this.updateApplication({
			idPassport: media,
		});
	}

	removeIdentity(): void {
		this.setIndentity(undefined);
	}

	setResidency(media?: Media): void {
		this.updateApplication({
			proofResidence: media,
		});
	}

	removeResidency(): void {
		this.setResidency(undefined);
	}

	setFinancialInformation(media?: Media): void {
		this.updateApplication({
			selfDeclarationFinancialNeed: media,
		});
	}

	removeFinancialInformation(): void {
		this.setFinancialInformation(undefined);
	}

	setVideoTeasing(media?: Media): void {
		this.updateApplication({
			videoId: media,
		});
	}

	removeVideoTeasing(): void {
		this.setVideoTeasing(undefined);
	}

	setDegreeId(degreeId: string): void {
		const degrees = this.application.get().degrees;
		this.updateApplication({
			degreeId,
			credits: degrees?.find((degree) => degree.id === degreeId)?.credits,
			credit: undefined,
		});
	}

	setLevelId(levelId: string): void {
		this.updateApplication({ levelId });
	}

	setDesiredStartDate(desiredStartDate: Date): void {
		this.updateApplication({ desiredStartDate });
	}

	setDegrees(degrees: ApplicationDegree[]): void {
		this.updateApplication({ degrees });
	}

	setCredits(): void {
		this.updateApplication({ credits: [] });
	}

	setCreditPoint(credit: string): void {
		this.updateApplication({
			credit,
		});
	}

	setResume(media?: Media): void {
		this.updateApplication({
			resume: media,
		});
	}

	removeResume(): void {
		this.setResume(undefined);
	}

	setEnglisCertificat(media?: Media): void {
		this.updateApplication({
			englishCertificat: media,
		});
	}

	removeEnglisCertificat(): void {
		this.setEnglisCertificat(undefined);
	}
	setIsCertificate(isCertificate: boolean): void {
		this.updateApplication({ isCertificate });
	}

	setDiploma(media?: Media): void {
		this.updateApplication({
			diploma: media,
		});
	}

	removeDiploma(): void {
		this.setDiploma(undefined);
	}

	setTranscriptRecord(media?: Media): void {
		this.updateApplication({
			transcriptRecord: media,
		});
	}

	removeTranscriptRecord(): void {
		this.setTranscriptRecord(undefined);
	}

	setWorkCertificate(media?: Media): void {
		this.updateApplication({
			workCertificate: media,
		});
	}

	removeWorkCertificate(): void {
		this.setWorkCertificate(undefined);
	}

	get isPersonalInformationValid(): Observable<boolean> {
		return Observable.from(this.application).transform(([application]) => {
			if (
				!application.firstName ||
				!application.familyName ||
				!application.nationality ||
				!application.countryResidence ||
				!application.dateBirth ||
				!application.email
			) {
				return false;
			}
			return true;
		});
	}

	get isStudyChoiceValid(): Observable<boolean> {
		return Observable.from(this.application).transform(([application]) => {
			if (!application.levelId || !application.credit || !application.degreeId || !application.desiredStartDate) {
				return false;
			}
			return true;
		});
	}

	get isUpluoadDocumentsValid(): Observable<boolean> {
		return Observable.from(this.application).transform(([application]) => {
			if (
				!application.idPassport ||
				!application.proofResidence ||
				!application.selfDeclarationFinancialNeed ||
				!application.resume ||
				!application.diploma
			) {
				return false;
			}
			return true;
		});
	}

	get isVideoStoryValid(): Observable<boolean> {
		return Observable.from(this.application).transform(([application]) => {
			if (!application.videoId) return false;
			return true;
		});
	}

	get isApplicationValid(): Observable<boolean> {
		return Observable.from(this.application).transform(([application]) => {
			if (
				!application.firstName ||
				!application.familyName ||
				!application.nationality ||
				!application.countryResidence ||
				!application.dateBirth ||
				!application.email ||
				!application.videoId ||
				!application.idPassport ||
				!application.proofResidence ||
				!application.selfDeclarationFinancialNeed ||
				!application.resume ||
				!application.diploma ||
				!application.levelId ||
				!application.credit ||
				!application.degreeId ||
				!application.desiredStartDate
			) {
				return false;
			}
			return true;
		});
	}
}
